import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import api from '../api';
import './ChatPage.css';
import { Send, MessageSquare, ArrowLeft, Edit2, Trash2, Reply, X, ShieldCheck, Flag } from 'lucide-react';
import StudentLayout from '../components/StudentLayout';
import ReportModal from '../components/ReportModal';
import { useFeatures } from '../components/FeatureGate';

const ChatPage = ({ embedded = false }) => {
    const { isSubEnabled } = useFeatures();
    // Server enforces the same policy (403); this only hides the composer.
    const canSend = isSubEnabled('messages', 'messages_send');
    const [conversations, setConversations] = useState([]);
    const [messages, setMessages] = useState([]);
    const [selectedUser, setSelectedUser] = useState(null); // { id, name, role }
    const [newMessage, setNewMessage] = useState('');
    const [loading, setLoading] = useState(true);
    const [replyingTo, setReplyingTo] = useState(null);
    const [editingMessage, setEditingMessage] = useState(null);
    const [hoveredMessageId, setHoveredMessageId] = useState(null);
    const [reportMessageModal, setReportMessageModal] = useState({ isOpen: false, messageId: null });
    const [filterTab, setFilterTab] = useState('all'); // 'all', 'professors', 'universities', 'support'
    const [showDirectory, setShowDirectory] = useState(false);
    const [directory, setDirectory] = useState([]);
    const [isSearching, setIsSearching] = useState(false);
    const [directorySearchTerm, setDirectorySearchTerm] = useState('');

    const [toasts, setToasts] = useState([]);
    const showToast = (message, type = 'success') => {
        const id = Date.now();
        setToasts(prev => [...prev, { id, message, type }]);
        setTimeout(() => {
            setToasts(prev => prev.filter(toast => toast.id !== id));
        }, 4000);
    };
    // For auto-scrolling
    const messagesEndRef = useRef(null);
    const location = useLocation();
    const navigate = useNavigate();

    const getDashboardName = () => {
        const role = localStorage.getItem('role')?.toLowerCase() || '';
        if (role === 'admin') return 'Admin Dashboard';
        if (role === 'university') return 'University Dashboard';
        if (role === 'student') return 'Student Dashboard';
        if (role === 'pop' || role === 'professor') return 'PoP Dashboard';
        return 'Dashboard';
    };

    useEffect(() => {
        if (!embedded) {
            const userRole = localStorage.getItem('role') || '';
            if (userRole.toLowerCase() === 'university') {
                navigate('/uni-dashboard', { replace: true, state: { showChat: true } });
            } else if (userRole.toLowerCase() === 'student') {
                navigate('/student-dashboard', { replace: true, state: { showChat: true } });
            } else if (userRole.toLowerCase() === 'pop' || userRole.toLowerCase() === 'professor') {
                navigate('/pop-dashboard', { replace: true, state: { showChat: true } });
            }
        }
    }, [embedded, navigate]);

    // Check if we navigated here with a specific user to chat with
    useEffect(() => {
        if (location.state?.startChatWith) {
            const { id, name, role } = location.state.startChatWith;
            // Optimistically set selected user even if conversation doesn't exist yet
            setSelectedUser({ other_user_id: id, other_user_name: name, other_user_role: role });
        }
    }, [location.state]);

    useEffect(() => {
        fetchConversations();
        const interval = setInterval(fetchConversations, 30000);
        return () => clearInterval(interval);
    }, []);

    useEffect(() => {
        setShowDirectory(false);
    }, [filterTab]);

    useEffect(() => {
        let interval;
        if (selectedUser) {
            const isBatch = selectedUser.other_user_role === 'batch';
            const targetId = isBatch ? selectedUser.batch_id : selectedUser.other_user_id;
            fetchMessages(targetId, isBatch);
            // Poll for new messages every 5 seconds if a chat is open
            interval = setInterval(() => {
                // Don't poll if editing to avoid overwriting input, or handle carefully
                if (!editingMessage) {
                    fetchMessages(targetId, isBatch);
                }
            }, 5000);
        }
        return () => clearInterval(interval);
    }, [selectedUser, editingMessage]);

    useEffect(() => {
        if (!editingMessage) {
            scrollToBottom();
        }
    }, [messages, editingMessage]);

    const handleContactSupport = async () => {
        try {
            const res = await api.get('/chat/support-contact');
            const admin = {
                other_user_id: res.data.id,
                other_user_name: res.data.name,
                other_user_role: res.data.role
            };
            setSelectedUser(admin);
            // If conversation exists in list, it will highlight, otherwise new chat starts
        } catch (err) {
            console.error(err);
            showToast("Support is currently unavailable", 'error');
        }
    };

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    const fetchConversations = async () => {
        try {
            const res = await api.get('/chat/conversations');
            setConversations(res.data);
            setLoading(false);
        } catch (err) {
            console.error("Failed to fetch conversations", err);
            setLoading(false);
        }
    };

    const fetchMessages = async (userId, isBatch = false) => {
        try {
            const endpoint = isBatch ? `/chat/batch-messages/${userId}` : `/chat/messages/${userId}`;
            const res = await api.get(endpoint);
            setMessages(res.data);
            window.dispatchEvent(new Event('chat_count_updated'));
        } catch (err) {
            console.error("Failed to fetch messages", err);
        }
    };

    const fetchDirectory = async (targetTab) => {
        let target = typeof targetTab === 'string' ? targetTab : filterTab;
        if (target === 'all' || target === 'support') {
            const role = localStorage.getItem('role');
            if (['university', 'faculty', 'tpo'].includes(role)) target = 'students';
            else if (role === 'student') target = 'faculties';
            else if (role === 'pop') target = 'universities';
            else if (role === 'admin') target = 'all-users';
            else target = 'professors';
        }

        setIsSearching(true);
        setShowDirectory(true);
        setDirectorySearchTerm('');
        try {
            const res = await api.get(`/chat/directory/${target}`);
            setDirectory(res.data);
        } catch (err) {
            console.error("Failed to fetch directory", err);
            showToast("Failed to load directory", 'error');
        } finally {
            setIsSearching(false);
        }
    };

    const handleSendMessage = async (e) => {
        e.preventDefault();
        if (!newMessage.trim() || !selectedUser) return;

        if (editingMessage) {
            // Handle Edit
            try {
                const res = await api.put(`/chat/messages/${editingMessage.id}?content=${encodeURIComponent(newMessage)}`);
                // Update local state
                setMessages(messages.map(m => m.id === editingMessage.id ? res.data : m));
                setEditingMessage(null);
                setNewMessage('');
            } catch (err) {
                console.error("Failed to edit message", err);
                showToast("Failed to edit message", 'error');
            }
            return;
        }

        // Handle Send (New or Reply)
        try {
            if (selectedUser.other_user_role === 'batch') {
                const payload = {
                    batch_id: selectedUser.batch_id,
                    content: newMessage
                };

                const tempMsg = {
                    id: Date.now(),
                    sender_id: 'me',
                    receiver_id: selectedUser.other_user_id,
                    content: newMessage,
                    created_at: new Date().toISOString(),
                    is_read: true,
                    reply_to_id: null,
                    is_edited: false,
                    is_deleted: false
                };

                setMessages([...messages, tempMsg]);
                setNewMessage('');
                setReplyingTo(null);

                await api.post('/chat/send/batch', payload);
                fetchMessages(selectedUser.batch_id, true);
                return;
            }

            const payload = {
                receiver_id: selectedUser.other_user_id,
                content: newMessage,
                reply_to_id: replyingTo ? replyingTo.id : null
            };

            // Optimistic update
            const tempMsg = {
                id: Date.now(),
                sender_id: 'me', // placeholder, technically backend handles ID check
                receiver_id: selectedUser.other_user_id,
                content: newMessage,
                created_at: new Date().toISOString(),
                is_read: false,
                reply_to_id: replyingTo ? replyingTo.id : null,
                is_edited: false,
                is_deleted: false
            };

            setMessages([...messages, tempMsg]);
            setNewMessage('');
            setReplyingTo(null);

            await api.post('/chat/send', payload);
            fetchMessages(selectedUser.other_user_id); // Sync with real ID and time
            fetchConversations(); // Update side bar snippet
        } catch (err) {
            console.error("Failed to send message", err);
            showToast("Failed to send message", 'error');
        }
    };

    const handleReply = (msg) => {
        setReplyingTo(msg);
        setEditingMessage(null);
        // Focus input
        document.querySelector('.chat-input-area input')?.focus();
    };

    const handleEdit = (msg) => {
        setEditingMessage(msg);
        setNewMessage(msg.content);
        setReplyingTo(null);
        document.querySelector('.chat-input-area input')?.focus();
    };

    const [messageToDelete, setMessageToDelete] = useState(null);

    // ... (keep existing useEffects)

    const handleDelete = (msgId) => {
        setMessageToDelete(msgId);
    };

    const confirmDelete = async () => {
        if (!messageToDelete) return;

        try {
            await api.delete(`/chat/messages/${messageToDelete}`);
            // Optimistic delete
            setMessages(messages.map(m => {
                if (m.id === messageToDelete) {
                    return { ...m, is_deleted: true, content: "This message was deleted" };
                }
                return m;
            }));
            setMessageToDelete(null);
            showToast("Message deleted", 'success');
        } catch (err) {
            console.error("Failed to delete message", err);
            showToast("Failed to delete message", 'error');
        }
    };

    // ... (keep helper functions)

    const cancelAction = () => {
        setReplyingTo(null);
        setEditingMessage(null);
        setNewMessage('');
    };

    // Helper to find replied message content
    const getRepliedMessageContent = (replyId) => {
        const msg = messages.find(m => m.id === replyId);
        return msg ? msg.content : "Original message not found";
    };

    const content = (
        <div className="chat-container" style={embedded ? { minHeight: 'calc(100vh - 120px)', width: '100%', background: 'transparent' } : {}}>
            <div className="chat-toasts-wrapper">
                {toasts.map(t => (
                    <div key={t.id} className={`chat-toast-message ${t.type}`}>
                        <span className="chat-toast-icon">{t.type === 'success' ? '✓' : '✕'}</span>
                        <span className="chat-toast-text">{t.message}</span>
                        <button className="chat-toast-close" onClick={() => setToasts(prev => prev.filter(item => item.id !== t.id))}>×</button>
                    </div>
                ))}
            </div>
            {/* Delete Confirmation Modal */}
            {messageToDelete && (
                <div className="modal-overlay">
                    <div className="chat-modal-content">
                        <h3>Delete Message</h3>
                        <p>Are you sure you want to delete this message? This action cannot be undone.</p>
                        <div className="modal-actions">
                            <button className="cancel-btn" onClick={() => setMessageToDelete(null)}>Cancel</button>
                            <button className="confirm-btn" onClick={confirmDelete}>Delete</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Report Message Modal */}
            {reportMessageModal.isOpen && (
                <ReportModal
                    isOpen={reportMessageModal.isOpen}
                    onClose={() => setReportMessageModal({ isOpen: false, messageId: null })}
                    contentType="message"
                    contentId={reportMessageModal.messageId}
                    onSuccess={() => showToast("Report submitted successfully", 'success')}
                />
            )}

            {/* TOP FULL-WIDTH HEADER CARD */}
            <div className="chat-top-header-box">
                <div className="top-header-top-row">
                    <div className="top-header-left">
                        <span className="chat-badge-pill">
                            <MessageSquare size={14} /> MESSAGES & DIRECTORY
                        </span>
                        <h2>Messages</h2>
                        <p>Chat directly with professors, universities, or student support.</p>
                    </div>
                    <div className="top-header-right-actions">
                        <button
                            className="top-action-btn new"
                            onClick={fetchDirectory}
                            title={localStorage.getItem('role') === 'pop' ? "Message University" : "Message Professor"}
                        >
                            <MessageSquare size={16} /> <span>New Chat</span>
                        </button>
                        {localStorage.getItem('role') !== 'admin' && (
                            <button
                                className="top-action-btn support"
                                onClick={handleContactSupport}
                                title="Contact Admin"
                            >
                                <ShieldCheck size={16} /> <span>Support</span>
                            </button>
                        )}
                        {!embedded && (
                            <button className="top-action-btn back-gradient" onClick={() => navigate(-1)} title={`Back to ${getDashboardName()}`}>
                                <ArrowLeft size={18} /> <span>{getDashboardName()}</span>
                            </button>
                        )}
                    </div>
                </div>

                {/* FILTER TABS BAR INSIDE TOP BOX */}
                <div className="chat-filter-tabs-bar">
                    <button className={filterTab === 'all' ? 'active' : ''} onClick={() => setFilterTab('all')}>All Conversations</button>

                    {['university', 'faculty', 'tpo'].includes(localStorage.getItem('role')) && (
                        <>
                            <button className={filterTab === 'students' ? 'active' : ''} onClick={() => setFilterTab('students')}>Students</button>
                            <button className={filterTab === 'batches' ? 'active' : ''} onClick={() => setFilterTab('batches')}>Batches</button>
                        </>
                    )}

                    {['university', 'student'].includes(localStorage.getItem('role')) && (
                        <>
                            <button className={filterTab === 'faculties' ? 'active' : ''} onClick={() => setFilterTab('faculties')}>Faculties</button>
                            <button className={filterTab === 'tpos' ? 'active' : ''} onClick={() => setFilterTab('tpos')}>TPO</button>
                        </>
                    )}

                    {localStorage.getItem('role') === 'student' && (
                        <button className={filterTab === 'uni-admins' ? 'active' : ''} onClick={() => setFilterTab('uni-admins')}>University</button>
                    )}

                    {localStorage.getItem('role') === 'university' && (
                        <button className={filterTab === 'professors' ? 'active' : ''} onClick={() => setFilterTab('professors')}>PoP</button>
                    )}

                    {['faculty', 'tpo', 'pop'].includes(localStorage.getItem('role')) && (
                        <button className={filterTab === 'universities' ? 'active' : ''} onClick={() => setFilterTab('universities')}>Universities</button>
                    )}

                    {localStorage.getItem('role') !== 'admin' && (
                        <button className={filterTab === 'support' ? 'active' : ''} onClick={() => setFilterTab('support')}>Support</button>
                    )}
                </div>
            </div>

            {/* TWO-COLUMN BODY (SIDEBAR LIST + MAIN WINDOW) */}
            <div className="chat-body-grid">
                {/* LEFT COLUMN: CONVERSATION LIST OR DIRECTORY */}
                <div className={`chat-sidebar-card ${selectedUser ? 'mobile-hidden' : ''}`}>
                    {showDirectory && (
                        <div className="directory-header" style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <span>Directory</span>
                                <button onClick={() => setShowDirectory(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}><X size={16} /></button>
                            </div>
                            <input 
                                type="text"
                                placeholder="Search contacts..."
                                value={directorySearchTerm}
                                onChange={(e) => setDirectorySearchTerm(e.target.value)}
                                style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid var(--border)', background: 'var(--bg-secondary)', color: 'var(--text-primary)', outline: 'none' }}
                            />
                        </div>
                    )}

                    <div className="conversation-list">
                        {loading || isSearching ? (
                            <div className="loading-state">{isSearching ? 'Accessing directory...' : 'Syncing messages...'}</div>
                        ) : showDirectory ? (
                            <div className="directory-container">
                                {(() => {
                                    const filteredDir = directory.filter(u => u.name.toLowerCase().includes(directorySearchTerm.toLowerCase()));
                                    if (filteredDir.length === 0) {
                                        return <div className="empty-state">No contacts found.</div>;
                                    }
                                    return filteredDir.map(user => (
                                        <div
                                            key={user.id}
                                            className="conversation-item"
                                            onClick={() => {
                                                setSelectedUser({ other_user_id: user.id, other_user_name: user.name, other_user_role: user.role, batch_id: user.batch_id });
                                                setShowDirectory(false);
                                            }}
                                        >
                                            <div className="avatar-circle">
                                                {user.name.charAt(0).toUpperCase()}
                                            </div>
                                            <div className="conv-info">
                                                <div className="conv-top">
                                                    <span className="conv-name">
                                                        {user.name}
                                                        <span className="role-tag">{user.role}</span>
                                                    </span>
                                                </div>
                                                <div className="last-msg">Click to start messaging</div>
                                            </div>
                                        </div>
                                    ));
                                })()}
                            </div>
                        ) : (
                            (() => {
                                const filtered = conversations.filter(conv => {
                                    if (filterTab === 'all') return true;
                                    if (filterTab === 'professors') return conv.other_user_role === 'pop';
                                    if (filterTab === 'universities' || filterTab === 'uni-admins') return conv.other_user_role === 'university';
                                    if (filterTab === 'support') return conv.other_user_role === 'admin';
                                    if (filterTab === 'students') return conv.other_user_role === 'student';
                                    if (filterTab === 'faculties') return conv.other_user_role === 'faculty';
                                    if (filterTab === 'batches') return conv.other_user_role === 'batch';
                                    if (filterTab === 'tpos') return conv.other_user_role === 'tpo';
                                    return false;
                                });

                                if (filtered.length === 0) {
                                    return (
                                        <div className="sidebar-empty-box">
                                            <div className="sidebar-dashed-inner">
                                                <MessageSquare size={36} style={{ color: 'var(--border-strong)', opacity: 0.6 }} />
                                                <p className="no-conv-text">No conversations</p>
                                                <button
                                                    className="find-contacts-btn"
                                                    onClick={fetchDirectory}
                                                >
                                                    Find Contacts
                                                </button>
                                            </div>
                                        </div>
                                    );
                                }

                                return (
                                    <>
                                        {filtered.map(conv => (
                                            <div
                                                key={conv.other_user_id}
                                                className={`conversation-item ${selectedUser?.other_user_id === conv.other_user_id ? 'active' : ''}`}
                                                onClick={() => setSelectedUser(conv)}
                                            >
                                                <div className="avatar-circle">
                                                    {conv.other_user_name.charAt(0).toUpperCase()}
                                                </div>
                                                <div className="conv-info">
                                                    <div className="conv-top">
                                                        <span className="conv-name">
                                                            {conv.other_user_name}
                                                            <span className="role-tag">{conv.other_user_role === 'pop' ? 'prof' : conv.other_user_role}</span>
                                                        </span>
                                                        {conv.unread_count > 0 && <span className="unread-badge">{conv.unread_count}</span>}
                                                    </div>
                                                    <p className="last-msg">{conv.last_message || 'No messages yet'}</p>
                                                </div>
                                            </div>
                                        ))}
                                        {filterTab !== 'all' && (
                                            <div style={{ padding: '10px 15px', textAlign: 'center' }}>
                                                <button
                                                    className="find-contacts-btn"
                                                    style={{ width: 'auto', padding: '8px 24px', fontSize: '0.9rem' }}
                                                    onClick={() => fetchDirectory(filterTab)}
                                                >
                                                    <MessageSquare size={14} style={{ display: 'inline', marginRight: '5px' }} />
                                                    Find More Contacts
                                                </button>
                                            </div>
                                        )}
                                    </>
                                );
                            })()
                        )}
                    </div>
                </div>

                {/* RIGHT COLUMN: ACTIVE CHAT WINDOW OR EMPTY SELECTOR */}
                <div className={`chat-main-card ${!selectedUser ? 'mobile-hidden' : ''}`}>
                    {selectedUser ? (
                        <div className="chat-main" style={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%' }}>
                            <div className="chat-main-header">
                                <button className="back-btn-mobile" onClick={() => setSelectedUser(null)}>
                                    <ArrowLeft size={20} />
                                </button>
                                <div className="avatar-circle small">
                                    {selectedUser.other_user_name?.charAt(0).toUpperCase()}
                                </div>
                                <div className="main-header-info">
                                    <h3>{selectedUser.other_user_name}</h3>
                                    <div className="main-header-status">
                                        {selectedUser.other_user_role === 'batch' ? (
                                            <span style={{ color: 'var(--primary)', fontWeight: '500' }}>Broadcast Mode (Announcements only)</span>
                                        ) : 'online'}
                                    </div>
                                </div>
                            </div>

                            <div className="messages-area">
                                {messages.length === 0 ? (
                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', color: 'var(--text-muted)', textAlign: 'center', padding: '20px' }}>
                                        <p>No messages yet. Say hi!</p>
                                    </div>
                                ) : (
                                    messages.map((msg, idx) => {
                                        const isMyMessage = msg.sender_id !== selectedUser.other_user_id;

                                        return (
                                            <div
                                                key={idx}
                                                className={`message-wrapper ${isMyMessage ? 'sent' : 'received'}`}
                                                onMouseEnter={() => setHoveredMessageId(msg.id)}
                                                onMouseLeave={() => setHoveredMessageId(null)}
                                            >
                                                <div className={`message-bubble ${isMyMessage ? 'sent' : 'received'} ${msg.is_deleted ? 'deleted' : ''}`}>
                                                    {/* Reply Quote */}
                                                    {msg.reply_to_id && !msg.is_deleted && (
                                                        <div className="reply-quote" onClick={() => {
                                                            const el = document.getElementById(`msg-${msg.reply_to_id}`);
                                                            el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                                        }}>
                                                            <div className="reply-bar"></div>
                                                            <span className="reply-text">{getRepliedMessageContent(msg.reply_to_id)}</span>
                                                        </div>
                                                    )}

                                                    <p>{msg.content}</p>

                                                    <div className="msg-meta">
                                                        <span className="msg-time">
                                                            {new Date(msg.created_at).toLocaleString([], {
                                                                day: '2-digit',
                                                                month: '2-digit',
                                                                year: 'numeric',
                                                                hour: '2-digit',
                                                                minute: '2-digit'
                                                            })}
                                                        </span>
                                                        {msg.is_edited && !msg.is_deleted && <span className="edited-tag">(edited)</span>}
                                                    </div>

                                                    {/* Action Menu Buttons */}
                                                    {hoveredMessageId === msg.id && !msg.is_deleted && (
                                                        <div className={`message-actions ${isMyMessage ? 'left' : 'right'}`}>
                                                            {canSend && (
                                                                <button onClick={() => handleReply(msg)} title="Reply">
                                                                    <Reply size={14} />
                                                                </button>
                                                            )}
                                                            {isMyMessage && canSend ? (
                                                                <>
                                                                    <button onClick={() => handleEdit(msg)} title="Edit">
                                                                        <Edit2 size={14} />
                                                                    </button>
                                                                    <button onClick={() => handleDelete(msg.id)} title="Unsend">
                                                                        <Trash2 size={14} />
                                                                    </button>
                                                                </>
                                                            ) : !isMyMessage ? (
                                                                <button onClick={() => setReportMessageModal({ isOpen: true, messageId: msg.id })} title="Report message" style={{ color: '#ef4444' }}>
                                                                    <Flag size={14} />
                                                                </button>
                                                            ) : null}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })
                                )}
                                <div ref={messagesEndRef} />
                            </div>

                            <div className="chat-footer">
                                {editingMessage && (
                                    <div className="action-banner">
                                        <div className="action-info">
                                            <Edit2 size={16} className="text-green-500" />
                                            <div className="action-text">
                                                <span className="action-label">Editing message</span>
                                                <span className="action-preview">{editingMessage.content}</span>
                                            </div>
                                        </div>
                                        <button className="close-action" onClick={cancelAction}>
                                            <X size={18} />
                                        </button>
                                    </div>
                                )}

                                {canSend ? (
                                    <form className="chat-input-area" onSubmit={handleSendMessage}>
                                        <div className="chat-input-group">
                                            <input
                                                type="text"
                                                placeholder={selectedUser.other_user_role === 'batch' ? "Broadcast to Batch..." : "Type a message..."}
                                                value={newMessage}
                                                onChange={(e) => setNewMessage(e.target.value)}
                                            />
                                            <button type="submit" disabled={!newMessage.trim()}>
                                                {editingMessage ? <Edit2 size={20} /> : <Send size={20} />}
                                            </button>
                                        </div>
                                    </form>
                                ) : (
                                    <div className="chat-input-area" style={{ justifyContent: 'center', color: 'var(--text-muted, #64748b)', fontSize: '13px', fontWeight: 600 }}>
                                        Messaging is disabled for your role — you can read conversations but not send.
                                    </div>
                                )}
                            </div>
                        </div>
                    ) : (
                        <div className="chat-main-empty-placeholder">
                            <MessageSquare size={48} style={{ color: 'var(--border-strong)', opacity: 0.7 }} />
                            <h4>Select a conversation to start chatting</h4>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );

    if (embedded) return content;
    const userRole = localStorage.getItem('role') || '';
    if (userRole.toLowerCase() === 'student') {
        return <StudentLayout>{content}</StudentLayout>;
    }
    return content;
};

export default ChatPage;



